import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { BRANCHES, JOBS_BY_BRANCH } from "../data/content";
import { ArrowLeft, ArrowRight, CheckCircle, Sparkle } from "@phosphor-icons/react";

export default function Branches() {
  return (
    <main className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-24">
      <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#0033A0]">Branches</p>
      <h1 className="font-display text-4xl md:text-6xl font-black tracking-tighter text-slate-900 mt-3">
        Six branches, six missions
      </h1>
      <p className="text-lg text-slate-700 mt-5 max-w-2xl leading-relaxed">
        Each branch has its own culture, jobs, and lifestyle. Tap one to see common career fields and basic-training details.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
        {BRANCHES.map((b, i) => (
          <div
            key={b.slug}
            data-testid={`branch-card-${b.slug}`}
            className="bg-white border border-slate-200 rounded-xl overflow-hidden hover:border-slate-900 hover:shadow-xl hover:-translate-y-1 transition-all fade-up flex flex-col"
            style={{ animationDelay: `${i * 60}ms` }}
          >
            <Link to={`/branches/${b.slug}`} className="group block">
              <div className="h-2" style={{ background: b.color }} />
              <div className="p-6">
                <h3 className="font-display text-2xl font-bold text-slate-900">{b.name}</h3>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed">{b.mission}</p>
                <div className="mt-5 text-xs text-slate-500 space-y-1">
                  <p><span className="font-semibold text-slate-700">Term:</span> {b.enlistmentTerm}</p>
                  <p><span className="font-semibold text-slate-700">Boot:</span> {b.basicTraining}</p>
                </div>
                <span className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-[#0033A0]">
                  See jobs <ArrowRight size={14} weight="bold" className="transition-transform group-hover:translate-x-0.5" />
                </span>
              </div>
            </Link>
            <div className="px-6 pb-5 mt-auto">
              <Link
                to={`/career-deepdive?branch=${b.slug}`}
                data-testid={`branch-card-deepdive-${b.slug}`}
                className="w-full inline-flex items-center justify-center gap-1.5 bg-[#D90429]/10 hover:bg-[#D90429] hover:text-white text-[#D90429] font-semibold text-sm py-2 px-3 rounded-md transition-colors"
              >
                <Sparkle size={14} weight="fill" /> $1 Career Deep Dive
              </Link>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-16 text-center">
        <Link to="/compare" data-testid="branches-to-compare" className="inline-flex items-center gap-2 bg-[#0033A0] hover:bg-[#002277] text-white font-semibold py-3 px-7 rounded-md transition-colors">
          Compare side-by-side <ArrowRight size={16} weight="bold" />
        </Link>
      </div>
    </main>
  );
}

export function BranchDetail() {
  const { slug } = useParams();
  const branch = BRANCHES.find((b) => b.slug === slug);
  if (!branch) return <Navigate to="/branches" replace />;

  const jobs = JOBS_BY_BRANCH[slug] || [];

  return (
    <main>
      <section className="relative" style={{ background: branch.color }}>
        <div className="max-w-5xl mx-auto px-6 md:px-10 py-20 md:py-28 text-white">
          <Link to="/branches" className="inline-flex items-center gap-2 text-sm text-white/80 hover:text-white" data-testid="back-to-branches">
            <ArrowLeft size={16} weight="bold" /> All branches
          </Link>
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-white/70 mt-8">Branch</p>
          <h1 className="font-display text-4xl md:text-6xl font-black tracking-tighter mt-3 leading-[1.05]">
            {branch.name}
          </h1>
          <p className="text-lg text-white/90 mt-5 max-w-2xl leading-relaxed">{branch.mission}</p>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 md:px-10 py-16 md:py-20 grid grid-cols-1 md:grid-cols-3 gap-8">
        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Enlistment term</h3>
          <p className="font-display text-xl font-bold text-slate-900 mt-2">{branch.enlistmentTerm}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-6 md:col-span-2">
          <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Basic training</h3>
          <p className="font-display text-xl font-bold text-slate-900 mt-2">{branch.basicTraining}</p>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 md:px-10 pb-20">
        <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-slate-900">Common jobs</h2>
        <p className="text-slate-600 mt-2">A small sample of the career fields available. Each branch has 100+ specialties.</p>

        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {jobs.map((j) => (
            <div key={j} className="flex items-center gap-3 bg-white border border-slate-200 rounded-md p-4" data-testid={`job-item-${j.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}>
              <CheckCircle size={18} weight="fill" className="text-[#0033A0] shrink-0" />
              <span className="text-sm font-medium text-slate-800">{j}</span>
            </div>
          ))}
        </div>

        <div className="mt-12 flex flex-wrap gap-3">
          <Link to={`/career-deepdive?branch=${branch.slug}`} data-testid={`branch-detail-deepdive-${branch.slug}`} className="inline-flex items-center gap-2 bg-[#D90429] hover:bg-[#B00020] text-white font-semibold py-3 px-6 rounded-md transition-colors">
            Get a $1 Career Deep Dive for {branch.name} <ArrowRight size={16} weight="bold" />
          </Link>
          <Link to="/compare" data-testid="branch-detail-compare" className="inline-flex items-center gap-2 bg-[#0033A0] hover:bg-[#002277] text-white font-semibold py-3 px-6 rounded-md transition-colors">
            Compare with other branches <ArrowRight size={16} weight="bold" />
          </Link>
          <a
            href="https://www.todaysmilitary.com/joining-eligibility/contact-a-recruiter"
            target="_blank" rel="noreferrer"
            data-testid="branch-detail-recruiter"
            className="inline-flex items-center gap-2 bg-white hover:bg-slate-100 text-slate-900 border border-slate-300 font-semibold py-3 px-6 rounded-md transition-colors"
          >
            Talk to a recruiter
          </a>
        </div>
      </section>
    </main>
  );
}
